import { isTablet } from 'react-native-device-info';
import palette from './colors';

// Semantic color mapping for better component usage
export const semanticColors = {
  // Primary brand (your business blue identity)
  primary: palette.blue[500], // #0088FE - active states, highlights
  primaryLight: palette.blue[200], // Light blue for backgrounds
  primaryDark: palette.blue[600], // #1560BD - headers, strong actions
  primarySurface: palette.blue[50], // #E8F2FF - tint behind a selected tile or chip

  // Secondary actions (orange for contrast/accent)
  secondary: palette.orange[500],
  secondaryLight: palette.orange[200],
  secondaryDark: palette.orange[700],

  // Success states
  success: palette.green[500],
  successLight: palette.green[200],
  successDark: palette.green[700],
  successDarker: palette.green[800],
  successDarkest: palette.green[900],

  successEmeraldSurface: palette.emerald[20],
  successEmeraldLight: palette.emerald[200],
  successEmerald: palette.emerald[600], // #059669 - emerald variant used in web
  successEmeraldDark: palette.emerald[700],
  successEmeraldDarker: palette.emerald[800],

  // Warning states
  warning: palette.amber[500],
  warningSurface: palette.amber[50],
  warningLight: palette.amber[200],
  warningDark: palette.amber[700],
  warningDarker: palette.amber[900],

  // Due / Outstanding states (brown-ish, warmer than warning amber)
  due: palette.orange[700],
  dueLight: palette.orange[200],
  dueDark: palette.orange[900],

  // Error states
  error: palette.red[500],
  errorSurface: palette.red[20],
  errorLight: palette.red[200],
  errorDark: palette.red[700],
  errorDarker: palette.red[900], // #B71C1C - dark red text actions, distinct from stock-problem red
  errorRipple: palette.red['500Alt'], // #FE0009 - ripple color variant

  // Info states
  info: palette.blue[400],
  infoLight: palette.blue[100],
  infoDark: palette.blue[600],
  infoAlt: palette.blue['600Alt'], // #2563EB - alternative blue-600 used in web

  // Creative/Premium
  premium: palette.purple[500],
  premiumLight: palette.purple[200],
  premiumDark: palette.purple[700],

  // Grey colors
  graphite: palette.gray[500],
  graphiteLight: palette.gray[200],
  graphiteDark: palette.gray[700],
};

// Light theme configuration
export const lightTheme = {
  colors: {
    // Semantic colors
    ...semanticColors,

    // Background colors
    background: palette.background,
    surface: palette.white,
    tableSecondary: palette.gray[50],
    surfaceSecondary: palette.surfaceInset,
    // Additional background variants
    backgroundGray50: palette.gray['50Alt'], // #F8FAFC - tailwind gray-50
    backgroundGray50Alt: palette.gray['50Alt2'], // #F9FAFB - another gray-50 variant
    backgroundGray100: palette.gray['100Alt'], // #F3F4F6 - tailwind gray-100

    // Neutral icon tiles (Quick Actions): borderless, colour-free so brand
    // accents stay reserved for the hero card and primary CTAs
    neutralTileBg: palette.gray['100Alt'],
    neutralTileIcon: palette.gray['700Alt'],

    // Text colors
    text: palette.gray[900],
    textSecondary: palette.gray[600],
    textTertiary: palette.gray[400],
    /** Placeholder / hint text: one step darker than textTertiary */
    textPlaceholder: palette.gray[500],
    textInverse: palette.white,
    // Additional text color variants
    textGray900: palette.gray['900Alt'], // #111827 - tailwind gray-900
    textGray600: palette.gray['600Alt'], // #4B5563 - tailwind gray-600
    textGray700: palette.gray['700Alt'], // #374151 - tailwind gray-700
    textGray500: palette.gray['500Alt'], // #6B7280 - tailwind gray-500

    // Border colors
    border: palette.gray[200],
    borderSecondary: palette.gray[100],
    borderGray: palette.gray[500],
    // Additional border variants
    borderGray200: palette.gray['200Alt'], // #E5E7EB - tailwind gray-200
    borderGray300: palette.gray['300Alt'], // #CCCCCC - simplified #ccc

    // Table specific
    tableHeader: palette.gray[200],
    tableHeaderText: palette.gray[900],

    ActiveSegmentedButton: palette.gray['300Alt'],

    // Interactive colors
    link: palette.link,
    linkHover: palette.blue[600],

    // Shadow colors
    shadow: palette.black,
    shadowLight: palette.gray[400],

    // Text colors for hover states
    whitesmoke: palette.whitesmoke,
  },

  // Spacing system (multiples of 4)
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
    xxxl: 64,
  },

  // Border radius system
  borderRadius: {
    sm: 4,
    md: 8,
    lg: 16,
    xl: 24,
    xxl: 32,
    round: 999,
  },

  // Typography scale
  typography: {
    h1: { fontSize: 32, fontWeight: 'bold' as const },
    h2: { fontSize: 28, fontWeight: 'bold' as const },
    h3: { fontSize: 24, fontWeight: 'bold' as const },
    h4: { fontSize: 20, fontWeight: '600' as const },
    h5: { fontSize: 18, fontWeight: '600' as const },
    h6: { fontSize: isTablet() ? 18 : 16, fontWeight: '600' as const },
    body: { fontSize: isTablet() ? 18 : 16, fontWeight: 'normal' as const },
    bodySmall: { fontSize: 14, fontWeight: 'normal' as const },
    caption: { fontSize: 12, fontWeight: 'normal' as const },
    button: { fontSize: isTablet() ? 18 : 16, fontWeight: '600' as const },
  },

  // Shadow presets - tinted with brand blue instead of black: reads as soft
  // ambient depth on the gray canvas rather than a muddy smudge
  shadows: {
    sm: {
      shadowColor: palette.blue[900],
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.08,
      shadowRadius: 3,
      elevation: 2,
    },
    md: {
      shadowColor: palette.blue[900],
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 12,
      elevation: 3,
    },
    lg: {
      shadowColor: palette.blue[900],
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.14,
      shadowRadius: 16,
      elevation: 6,
    },
  },

  // Common component styles for reusability across screens
  components: {
    // Elevated cards get shadow only - a border on top of a shadow mutes the
    // shadow and flattens the card. Borders belong on flat inset surfaces.
    card: {
      backgroundColor: palette.white,
      borderRadius: 16, // Use lg border radius consistently
      shadowColor: palette.blue[900],
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 12,
      elevation: 3,
      borderWidth: 0,
    },
    productCard: {
      backgroundColor: palette.white,
      borderRadius: 16, // Consistent with HomeScreen cards
      shadowColor: palette.blue[900],
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.08,
      shadowRadius: 3,
      elevation: 2,
      borderWidth: 0,
    },
  },
};

// Dark theme configuration
export const darkTheme = {
  ...lightTheme,
  colors: {
    // Start from light theme colors to preserve required keys
    ...lightTheme.colors,

    // Background colors (inverted)
    background: palette.gray[900],
    surface: palette.gray[800],
    surfaceSecondary: palette.gray[700],

    // Neutral icon tiles (inverted)
    neutralTileBg: palette.gray[700],
    neutralTileIcon: palette.gray[200],

    // Text colors (inverted)
    text: palette.white,
    textSecondary: palette.gray[300],
    textTertiary: palette.gray[500],
    textPlaceholder: palette.gray[700],
    textInverse: palette.gray[900],

    // Border colors (darker)
    border: palette.gray[600],
    borderSecondary: palette.gray[700],
    borderGray: palette.gray[600],

    // Interactive colors
    link: palette.blue[300],
    linkHover: palette.blue[200],

    // Shadow colors
    shadow: palette.black,
    shadowLight: palette.gray[800],

    // Table specific (darker greys for dark mode)
    tableHeader: palette.gray[700],
    tableHeaderText: palette.white,
  },
};

// Theme type for TypeScript
export type Theme = typeof lightTheme;

// Default export
export default lightTheme;
